import { useEffect, useState } from 'react';
import { api, type CatalogLineRow } from '../lib/api';

/**
 * Shared, memoised access to the FP&A line catalog's labels.
 *
 * WHY MEMOISED AT MODULE LEVEL. The consumer is LineCodeMapSelect, which
 * renders once per QBO account row — dozens on the Accounts tab. A useQuery in
 * the component would fire one request per row, because they all mount in the
 * same tick and would all miss a time-based cache. One module-level promise
 * means one request for the page, shared by every row.
 *
 * WHY IT EXISTS AT ALL. The mapping picker's own list of line codes is a
 * hard-coded set in lib/fpaLineCodes, so any code registered in the DB catalog
 * rendered as its raw code — a QBO account mapped to `revenue.var.grants` read
 * as "revenue.var.grants" instead of "Grants received". The catalog is the
 * source of truth for what a line is called; this is how the web reaches it
 * without every surface fetching for itself.
 */

let inflight: Promise<CatalogLineRow[]> | null = null;

function load(): Promise<CatalogLineRow[]> {
  if (!inflight) {
    inflight = api.fpa.catalog()
      .then((r) => r.catalog)
      // Don't cache a failure: the next caller should get a fresh attempt.
      .catch((e) => { inflight = null; throw e; });
  }
  return inflight;
}

/** Drop the memo so the next read refetches — call after a catalog write. */
export function invalidateCatalogLabels(): void {
  inflight = null;
}

export interface CatalogLabels {
  /** The catalog label for a code, or the code itself if it has none. */
  labelOf: (code: string | null) => string;
  /** Active leaves, for a picker. Sorted the way the statement reads. */
  leaves: CatalogLineRow[];
  loaded: boolean;
}

export function useCatalogLabels(): CatalogLabels {
  const [rows, setRows] = useState<CatalogLineRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    load().then((r) => { if (alive) setRows(r); }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);

  const byCode = new Map((rows ?? []).map((r) => [r.code, r.label]));
  return {
    labelOf: (code) => (code ? (byCode.get(code) ?? code) : 'Unmapped'),
    leaves: (rows ?? [])
      .filter((r) => r.kind === 'leaf' && r.is_active)
      .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
    loaded: rows !== null,
  };
}
